/**
 * TapPay Web SDK 動態載入工具。
 * 邏輯抽自 CardForm.tsx:78-97：window.TPDirect 已存在 → 立即 resolve；
 * 否則注入 <script src=REACT_APP_TAPPAY_SCRIPT_URL>，load 後 resolve、error 則 reject。
 * 重複呼叫共用同一個 Promise，只會注入一次 script。
 *
 * declare global Window.TPDirect 集中宣告於此（CardForm.tsx / Kiosk/AddCard.tsx 原各自宣告一份，
 * 為避免同 project 內重複宣告衝突，已移除那兩處，統一以本檔為單一來源）。
 */

declare global {
  interface Window {
    TPDirect: any;
    /** loadTapPaySdk 內部快取的載入 Promise，供重複呼叫共用、避免重複注入 script。 */
    __tappaySdkLoadPromise?: Promise<void>;
    /** setupSDK 是否已呼叫過的標記（useApplePay 用來避免與 CardForm 既有邏輯重複 setup）。 */
    __tappaySdkSetup?: boolean;
  }
}

/** TapPay serverType：REACT_APP_TAPPAY_SERVER_TYPE=production 才用正式環境，其餘一律 sandbox。 */
export const TAPPAY_SERVER_TYPE: "sandbox" | "production" =
  process.env.REACT_APP_TAPPAY_SERVER_TYPE === "production" ? "production" : "sandbox";

/** existingScript 分支輪詢 TPDirect 是否就緒的間隔／逾時（見下方說明）。 */
const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 5000;

export function loadTapPaySdk(): Promise<void> {
  if (window.TPDirect) {
    return Promise.resolve();
  }

  if (window.__tappaySdkLoadPromise) {
    return window.__tappaySdkLoadPromise;
  }

  // 測試時需要每次呼叫都讀最新的 env（beforeEach 動態設定），故不提到模組頂層常數。
  const scriptUrl = process.env.REACT_APP_TAPPAY_SCRIPT_URL || "";

  const promise = new Promise<void>((resolve, reject) => {
    // 失敗（含下面輪詢逾時）時：移除壞掉的 <script> 標籤、清掉快取的 Promise，
    // 否則下一次呼叫會在 existingScript 分支撞見一個 load/error 事件都不會再觸發的
    // 殘骸（同一個 <script> 對同一組事件只會 fire 一次），永遠卡住、無法重試。
    const failWith = (script: HTMLScriptElement | null, err: Error) => {
      if (script) script.remove();
      window.__tappaySdkLoadPromise = undefined;
      reject(err);
    };

    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`) as HTMLScriptElement | null;

    if (existingScript) {
      // script 標籤已存在，但 load／error 事件可能早就在別次呼叫時觸發過（此時我們才剛掛上
      // 監聽器，事件不會重放），導致 TPDirect 其實已經就緒或已經確定不會就緒，卻永遠等不到事件。
      // 監聽器保留給「這次呼叫真的搶到還沒 fire 過的事件」的情況，另外用輪詢兜底。
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => failWith(existingScript, new Error("TapPay SDK 載入失敗")), { once: true });

      const startedAt = Date.now();
      const poll = () => {
        if (window.TPDirect) {
          resolve();
          return;
        }
        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
          failWith(existingScript, new Error("TapPay SDK 載入逾時"));
          return;
        }
        window.setTimeout(poll, POLL_INTERVAL_MS);
      };
      window.setTimeout(poll, POLL_INTERVAL_MS);
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => failWith(script, new Error("TapPay SDK 載入失敗")), { once: true });
    document.body.appendChild(script);
  });

  window.__tappaySdkLoadPromise = promise;
  return promise;
}
